import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { get, getObjectWithVariants } from '@theme-ui/css';
import { Overlay, OverlayContext } from '@uswitch/spark.overlay';
import { Type } from '@uswitch/spark.type';
import { getVariantPath } from '@uswitch/spark.utils';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import FocusTrap from 'focus-trap-react';
import PropTypes from 'prop-types';
import React from 'react';
import { useThemeUI } from 'theme-ui';
import { jsx as _jsx } from "theme-ui/jsx-runtime";
import { jsxs as _jsxs } from "theme-ui/jsx-runtime";
export { ModalClose } from './modal-close';
var DEFAULT_TRANSITION_TIMEOUT = 300;
var wrapperVariantPath = getVariantPath({
  id: 'modal',
  element: 'wrapper'
});
var closeVariantPath = getVariantPath({
  id: 'modal',
  element: 'close'
});
var titleVariantPath = getVariantPath({
  id: 'modal',
  element: 'title'
});
var contentVariantPath = getVariantPath({
  id: 'modal',
  element: 'content'
});
var footerVariantPath = getVariantPath({
  id: 'modal',
  element: 'footer'
});
var modalSettingsVariantPath = getVariantPath({
  id: 'modal',
  element: 'modalSettings'
});
export var Modal = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var _modalSettings$titleT, _modalSettings$conten, _modalSettings$transi;

  var _ref$as = _ref.as,
      As = _ref$as === void 0 ? 'div' : _ref$as,
      overlay = _ref.overlay,
      closeButton = _ref.closeButton,
      footer = _ref.footer,
      title = _ref.title,
      _children = _ref.children,
      props = _objectWithoutProperties(_ref, ["as", "overlay", "closeButton", "footer", "title", "children"]);

  var divRef = React.useRef();
  var setDivRef = React.useCallback(function (node) {
    if (divRef.current) {
      // old non-null div - do cleanup
      enableBodyScroll(divRef.current);
    }

    if (node) {
      // new non-null node
      disableBodyScroll(node, {
        reserveScrollBarGap: true
      });
    } // save new div


    divRef.current = node;
  }, []);

  var _useThemeUI = useThemeUI(),
      theme = _useThemeUI.theme;

  var modalSettings = getObjectWithVariants(get(theme, modalSettingsVariantPath()), theme);
  var TitleType = Type[(_modalSettings$titleT = modalSettings === null || modalSettings === void 0 ? void 0 : modalSettings.titleType) !== null && _modalSettings$titleT !== void 0 ? _modalSettings$titleT : 'H6'];
  var ContentType = Type[(_modalSettings$conten = modalSettings === null || modalSettings === void 0 ? void 0 : modalSettings.contentType) !== null && _modalSettings$conten !== void 0 ? _modalSettings$conten : 'Body1'];
  return /*#__PURE__*/React.cloneElement(overlay, {
    transitionTimeout: (_modalSettings$transi = modalSettings === null || modalSettings === void 0 ? void 0 : modalSettings.transitionTimeout) !== null && _modalSettings$transi !== void 0 ? _modalSettings$transi : DEFAULT_TRANSITION_TIMEOUT
  }, _jsx(OverlayContext.Consumer, {
    children: function children(_ref2) {
      var active = _ref2.active;
      return _jsx(FocusTrap, {
        focusTrapOptions: {
          clickOutsideDeactivates: true,
          escapeDeactivates: true,
          checkCanFocusTrap: function checkCanFocusTrap(trapContainers) {
            var results = trapContainers.map(function (trapContainer) {
              return new Promise(function (resolve) {
                var interval = setInterval(function () {
                  if (getComputedStyle(trapContainer).visibility !== 'hidden') {
                    resolve();
                    clearInterval(interval);
                  }
                }, 5);
              });
            });
            return Promise.all(results);
          }
        },
        children: _jsxs(As, _objectSpread(_objectSpread({
          tabIndex: "-1",
          sx: {
            variant: wrapperVariantPath(active ? 'active' : 'initial')
          },
          onClick: function onClick(e) {
            e.stopPropagation();
          }
        }, props), {}, {
          ref: ref,
          children: [closeButton && _jsx("div", {
            sx: {
              variant: closeVariantPath()
            },
            children: closeButton
          }), title && _jsx(TitleType, {
            sx: {
              variant: titleVariantPath()
            },
            children: title
          }), _jsx(ContentType, {
            as: "div",
            sx: {
              variant: contentVariantPath()
            },
            ref: setDivRef,
            children: _children
          }), footer && _jsx("div", {
            sx: {
              variant: footerVariantPath(),
              marginTop: 'auto'
            },
            children: footer
          })]
        }))
      });
    }
  }));
});
Modal.propTypes = {
  /**
   * `Overlay` element to use
   */
  overlay: PropTypes.element,

  /**
   * `ModalClose` button to use
   */
  closeButton: PropTypes.element,

  /**
   * Modal title
   */
  title: PropTypes.string,

  /**
   * Element rendered in the non-scrolling, bottom part of the modal
   */
  footer: PropTypes.element
};
Modal.defaultProps = {
  overlay: _jsx(Overlay, {})
};